import React, { useState } from "react";
import Text from "../../Tags/Text";
import { Badge } from "../../ui/badge";
import { useTableData } from "../../../hooks/useTableData";
import { getUsageBillingLog } from "../../../apis/workspace/billing";
import { isEmptyObject, returnArray } from "../../../utils/GenericFn";
import Filter from "../../Filter";
import EditColumn from "../../EditColumn";
import { ArrowDown, ArrowUp, Eye, ExternalLink } from "lucide-react";
import PrintDate from "../../Tags/PrintDate";
import TableLoader from "../../Loaders/TableLoader";
import NoData from "../../NoData";
import Pagination from "../../Pagination";
import Image from "../../Tags/Image";
import { useTheme } from "../../ThemeProvider";
import { getInitials } from "../../../utils/helper";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "../../ui/hover-card";

export default function UsageLogs({ activeWorkspace }) {
  const [usageLog, setUsageLog] = useState({});
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [openHoverCardId, setOpenHoverCardId] = useState(null);
  const { theme } = useTheme();
  const {
    data,
    paginationInfo,
    filterMetadata,
    columnMetadata,
    apiResponse,
    isLoading,
    isFetching,
    isFetched,
    isSuccess,
    isError,
    filters,
    sort,
    pagination,
    columns,
    sortedVisibleColumns,
    getPageNumber,
    getFiltersObject,
    handleSetSort,
    getColumns,
  } = useTableData({
    queryKey: "get_calls_list",
    fetchFn: getUsageBillingLog,
    defaultHeaders: { "X-Workspace-Id": activeWorkspace?.id },
    enabled: !!activeWorkspace?.id,
    onSuccess: (data) => {
      //   const mappedData = mapApiResponseToTableData(data);
      console.log("data", data);
      setUsageLog(data);
      setIsInitialLoad(false);

      console.log("Fetched!", data);
    },
    onError: (error) => {
      console.error("CallLogs API error:", error);
      if (error?.status === 403 || error?.response?.status === 403) {
        setIsInitialLoad(false);
      } else {
      }
    },
  });

  const getTooltipContent = (context, user) => {
    if (!context) return null;

    const { type, label, metadata } = context;
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const appHome = process.env.REACT_APP_APP_HOME;
    const workspaceSlug = activeWorkspace?.slug;

    switch (type) {
      case "contact_enrichment_bulk":
      case "company_enrichment_bulk":
        return {
          title: label,
          subtitle: `${metadata?.record_count} Records`,
          link:
            metadata?.enrichment_list_id && workspaceSlug
              ? `${baseUrl}/${appHome}/${workspaceSlug}/enrich/list/${metadata.enrichment_list_id}`
              : null,
          linkLabel: "View enrichment list",
          icon: theme === "dark" ? "/images/dark-bulk-contact-enrichment.svg" : "/images/bulk-icon.svg",
        };
      case "contact_enrichment_single":
        return {
          title: label,
          subtitle: metadata?.name,
          link: metadata?.linkedin_url,
          linkLabel: "LinkedIn Profile",
          icon: theme === "dark" ? "/images/dark-single-contact-enrichment.svg" : "/images/user-icon.svg",
        };
      case "company_enrichment":
        return {
          title: label,
          subtitle: metadata?.domain,
          link: metadata?.linkedin_url,
          linkLabel: `${metadata?.domain}`,
          icon: theme === "dark" ? "/images/dark-company-enrichment.svg" : "/images/company.svg",
        };
      default:
        return null;
    }
  };

  const renderCellContent = (columnKey, item) => {
    console.log("columnKey", columnKey);
    switch (columnKey) {
      case "user":
        return (
          <div className="flex items-center">
            <div className="h-9 w-9 flex-shrink-0">
              {item?.[columnKey].profile_photo_url ? (
                <Image
                  nobaseurl
                  className="h-9 w-9 rounded-full"
                  src={item?.[columnKey].profile_photo_url}
                  alt=""
                />
              ) : (
                <div className="h-9 w-9 flex-shrink-0 items-center bg-gray-100 border border-border justify-center inline-flex rounded-full text-xs">
                  {getInitials(item?.[columnKey]?.name)}
                </div>
              )}
            </div>
            <div className="ml-4">
              <div className="font-medium ">{item?.[columnKey]?.name}</div>
              <div className="mt-0 text-gray-500">{item?.[columnKey]?.email}</div>
            </div>
          </div>
        );
      case "created_at":
        return <PrintDate date={item?.[columnKey]} showTime={true} />;

      case "credits_used":
        return <span>{item?.[columnKey]?.total}</span>;

      case "context_details":
        const hasContext = item?.context && Object.keys(item.context).length > 0;
        if (!hasContext) return "-";

        const tooltipData = getTooltipContent(item.context, item.user);
        if (!tooltipData) return "-";

        return (
          <HoverCard
            open={openHoverCardId === item.id}
            onOpenChange={(isOpen) => setOpenHoverCardId(isOpen ? item.id : null)}
            openDelay={200}
            closeDelay={50}
          >
            <HoverCardTrigger asChild>
              <button className="focus:outline-none">
                <Eye className="h-4 w-4 text-gray-400 cursor-pointer hover:text-gray-600 transition-colors" />
              </button>
            </HoverCardTrigger>
            <HoverCardContent side="left" align="center" sideOffset={10} className="py-4 px-3">
              <div className="flex gap-2">
                <div className="flex-shrink-0">
                  <Image src={tooltipData.icon} className="h-10 w-10 no-invert" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-xs font-semibold text-[#181D27] dark:text-white mb-1">
                    {tooltipData.title}
                  </h3>
                  {tooltipData.subtitle && (
                    <p className="text-xs text-[#535862] dark:text-[#94979C] mb-1">{tooltipData.subtitle}</p>
                  )}
                  {tooltipData.link && (
                    <a
                      href={tooltipData.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-[#6941C6] dark:text-[#7F56D9] font-medium hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {tooltipData.linkLabel}
                      <ExternalLink className="h-2.5 w-2.5" />
                    </a>
                  )}
                </div>
              </div>
            </HoverCardContent>
          </HoverCard>
        );
      default:
        return item?.[columnKey] || "-";
    }
  };

  return (
    <div className="pt-6 border-t mt-8">
      <Text size="md">Full usage log</Text>
      {usageLog?.data && (usageLog.data.length > 0 || !isEmptyObject(filters)) && (
        <div className="flex justify-between items-center mt-4">
          <div className="space-x-3 inline-flex items-center">
            {usageLog.filterMetadata && (
              <>
                <span className="text-sm text-gray-500">Filters:</span>
                <Filter filterMetadata={usageLog.filterMetadata} getFiltersObject={getFiltersObject} />
              </>
            )}
          </div>
          <div className="space-x-3">
            {usageLog.columnMetadata && (
              <EditColumn columnMetadata={usageLog.columnMetadata} getColumns={getColumns} />
            )}
          </div>
        </div>
      )}
      {usageLog?.data && usageLog.data.length > 0 && isSuccess && (
        <div className="mt-4 flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <div className="overflow-hidden border border-border rounded-[--radius]">
                <table className="min-w-full divide-y divide-border table-fixed">
                  <thead className="bg-gray-100">
                    <tr>
                      {returnArray(sortedVisibleColumns).map((column, index) => (
                        <th
                          key={index}
                          scope="col"
                          className={`px-3 py-3.5 text-left text-xs font-medium text-text-600 ${
                            column.key === "user_name" ? "w-1/4" : ""
                          } ${column.sortable ? "cursor-pointer" : ""}`}
                          onClick={() => (column.sortable ? handleSetSort(column.key) : void 0)}
                        >
                          <span className={"inline-flex items-center"}>
                            {column.display_name}
                            {column.sortable && (
                              <>
                                {sort === `-${column.key}` ? (
                                  <ArrowDown className="inline-block ml-1 h-3 w-3" />
                                ) : sort === column.key ? (
                                  <ArrowUp className="inline-block ml-1 h-3 w-3" />
                                ) : (
                                  <ArrowDown className="inline-block ml-1 h-3 w-3 opacity-50" />
                                )}
                              </>
                            )}
                          </span>
                        </th>
                      ))}
                      <th scope="col" className="!px-3 !py-4 text-left text-sm font-medium" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border bg-background">
                    {usageLog.data &&
                      usageLog.data.length > 0 &&
                      returnArray(usageLog.data).map((item, index) => (
                        <tr key={item.id} className={`hover:bg-gray-50  ${index % 2 !== 0 ? "" : ""}`}>
                          {returnArray(sortedVisibleColumns).map(({ key: colKey }) => (
                            <td
                              key={colKey}
                              className="whitespace-nowrap px-3 py-4 text-sm text-gray-900 dark:text-[#F7F7F7] "
                            >
                              {renderCellContent(colKey, item)}
                            </td>
                          ))}
                          <td className="whitespace-nowrap px-3 py-4">
                            {renderCellContent("context_details", item)}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
      {(isLoading || isInitialLoad) && <TableLoader marginTop="mt-4" />}

      {usageLog?.data?.length === 0 && !isLoading && !isError && <NoData title="No data found" />}

      {/* Pagination */}
      <div className="flex items-center justify-between rounded-[--radius] py-3">
        <div className="flex-1">
          {usageLog.pagination && usageLog.pagination.total > 0 && (
            <Pagination data={usageLog.pagination} getPageNumber={getPageNumber} />
          )}
        </div>
      </div>
    </div>
  );
}
